import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SeoHead from "./SiteSeo";
import SiteLayout from "./SiteLayout";
import { CalendlyLink, SmartLink } from "./SiteHeader";
import { Faq, FinalCta } from "./sections";
import { toolBySlug } from "./calculators/tools";
import ComingSoonPage from "./ComingSoonPage";
import UnavailablePage from "./UnavailablePage";
import { getPublic } from "./publicFetch";
import { PlaybookTile } from "./PlaybooksHub";
import { API, usePlaybooks } from "./playbookData";

const SITE = "https://www.hellomortgage.ca";

export function PlaybookDownload({ p, compact = false }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [state, setState] = useState("idle");
  const href = `${API}${p.download_url}`;
  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setState("sending");
    try {
      const r = await fetch(`${API}/api/public/playbooks/${p.slug}/email`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, page_path: window.location.pathname }) });
      setState(r.ok ? "sent" : "error");
    } catch { setState("error"); }
  };
  return (
    <div className={`pb-download${compact ? " compact" : ""}`} data-testid="playbook-download">
      <a className="btn" href={href} target="_blank" rel="noopener" data-testid="playbook-download-btn">Download the PDF</a>
      <span className="pb-meta">{p.pages} pages · free · no sign-up needed</span>
      {p.gate === "soft" && state !== "sent" && (
        <form className="pb-email" onSubmit={submit} data-testid="playbook-email-form">
          <label className="pb-email-label">Want it in your inbox for later? Optional.</label>
          <div className="pb-email-row">
            <input type="text" placeholder="First name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="playbook-email-name" />
            <input type="email" required placeholder="you@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="playbook-email-input" />
            <button type="submit" className="btn outline" disabled={state === "sending"} data-testid="playbook-email-submit">{state === "sending" ? "Sending…" : "Email it to me"}</button>
          </div>
          {state === "error" && <small className="pb-err" data-testid="playbook-email-error">That didn’t send — the download button above still works.</small>}
        </form>
      )}
      {state === "sent" && <p className="pb-sent" data-testid="playbook-email-sent">Sent. Check your inbox — and reply to that email any time a question comes up.</p>}
    </div>
  );
}

export default function PlaybookPage() {
  const slug = useLocation().pathname.split("/").filter(Boolean)[1];
  const list = usePlaybooks();
  const [p, setP] = useState(undefined);
  useEffect(() => {
    getPublic(`/playbooks/${slug}`).then((r) => setP(r.data || (r.error ? "error" : null)));
  }, [slug]);
  if (p === undefined) return <SiteLayout loading><section className="hero"><div className="wrap"><div className="eyebrow">Loading…</div></div></section></SiteLayout>;
  if (p === null) return <ComingSoonPage />;
  if (p === "error") return <UnavailablePage title={`${slug.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())} Playbook · Hello Mortgage Team`} />;

  const calc = toolBySlug(p.calculator);
  const others = (list || []).filter((o) => o.slug !== slug).slice(0, 3);
  const ld = [
    { "@context": "https://schema.org", "@type": "DigitalDocument", name: p.label, alternateName: p.subtitle, description: p.meta_description, url: `${SITE}/playbooks/${slug}/`,
      encodingFormat: "application/pdf", numberOfPages: p.pages, inLanguage: "en-CA", isAccessibleForFree: true, image: p.cover_url ? `${SITE}${p.cover_url}` : undefined,
      author: { "@type": "Organization", name: "Hello Mortgage Team" }, publisher: { "@type": "Organization", name: "Hello Mortgage Team" }, audience: { "@type": "Audience", audienceType: p.audience },
      hasDigitalDocumentPermission: { "@type": "DigitalDocumentPermission", permissionType: "https://schema.org/ReadPermission", grantee: { "@type": "Audience", audienceType: "public" } } },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` }, { "@type": "ListItem", position: 2, name: "Playbooks", item: `${SITE}/playbooks/` }, { "@type": "ListItem", position: 3, name: p.label, item: `${SITE}/playbooks/${slug}/` }] },
    { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: p.faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
  ];

  return (
    <SiteLayout>
      <SeoHead title={p.seo_title} description={p.meta_description} path={`/playbooks/${slug}/`} ogImage={p.cover_url ? `${API}${p.cover_url}` : undefined} />
      <Helmet><script type="application/ld+json">{JSON.stringify(ld)}</script></Helmet>
      <nav className="wrap crumbs" aria-label="Breadcrumb" data-testid="playbook-breadcrumb"><SmartLink href="/">Home</SmartLink><span>›</span><SmartLink href="/playbooks/">Playbooks</SmartLink><span>›</span><span aria-current="page">{p.label}</span></nav>
      <section className="hero pb-page-hero" data-testid={`playbook-${slug}`}>
        <div className="hero-grid wrap pb-hero-grid">
          <div>
            <div className="eyebrow">Mortgage Playbook · {p.audience}</div>
            <h1 style={{ fontSize: "clamp(2.8rem,5vw,4.6rem)" }}>{p.label}</h1>
            <p className="pb-subtitle">{p.subtitle}.</p>
            <p className="pb-tagline">{p.tagline}</p>
            <PlaybookDownload p={p} />
          </div>
          <aside className="pb-cover-card" aria-hidden="true">{p.cover_url && <img src={`${API}${p.cover_url}`} alt="" />}</aside>
        </div>
      </section>
      <section className="solution-body pb-body">
        <div className="wrap solution-grid">
          <article className="solution-content" data-testid="playbook-content">
            <h2>What this playbook covers</h2>
            {p.summary.map((para, i) => <p key={i}>{para}</p>)}
            <h2>What’s inside</h2>
            <ul className="pb-inside" data-testid="playbook-inside">{p.inside.map((line) => <li key={line}>{line}</li>)}</ul>
            {p.preview_urls?.length > 0 && (
              <>
                <h2>A look inside</h2>
                <div className="pb-previews" data-testid="playbook-previews">{p.preview_urls.map((u, i) => <img key={u} src={`${API}${u}`} alt={`${p.label} — page ${i + 2}`} loading="lazy" />)}</div>
              </>
            )}
            <h2>Good questions</h2>
            <Faq items={p.faqs} />
          </article>
          <aside className="solution-rail">
            <div className="rail-card rail-talk">
              <h3>Read it. Then let’s talk.</h3>
              <p>The playbook covers the general rules. Your income, credit and timeline decide what actually applies — and that conversation is free.</p>
              <CalendlyLink className="btn" testId="playbook-rail-cta">Let's Get This Going</CalendlyLink>
            </div>
            <SmartLink className="rail-card rail-calc" href={p.solution} data-testid="playbook-solution-link"><span className="tag">Related solution</span><h3>{p.solution_label}</h3><span className="tool-link">See how we help →</span></SmartLink>
            {calc && <SmartLink className="rail-card rail-calc" href={`/calculators/${calc.slug}/`} data-testid="playbook-calculator-link"><span className="tag">{calc.name} calculator</span><h3>{calc.tagline}</h3><span className="tool-link">Run the numbers →</span></SmartLink>}
          </aside>
        </div>
      </section>
      {others.length > 0 && (
        <section className="pb-more">
          <div className="wrap">
            <div className="eyebrow">More playbooks</div>
            <div className="pb-grid compact" data-testid="playbook-more">{others.map((o) => <PlaybookTile p={o} key={o.slug} compact />)}</div>
          </div>
        </section>
      )}
      <FinalCta text="Ready to turn the playbook into a plan? Start with a free conversation. No pressure. No mortgage-speak." />
    </SiteLayout>
  );
}
